import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Save, X, Clock, Users, Moon, Sun, Power, Loader2, Trash2 } from 'lucide-react';
import { api } from '@/api/client';

interface Shift {
  id: number;
  name: string;
  startTime: string;
  endTime: string;
  crossesMidnight: boolean;
  durationMinutes: number;
  description?: string | null;
  isActive: boolean;
  label: string;
  durationLabel: string;
  employeeCount: number;
}

const blank = { name: '', startTime: '09:30', endTime: '18:30', description: '', isActive: true };

/**
 * Shift master — the named working hours an employee can be put on.
 *
 * The one thing worth showing loudly is a shift that runs past midnight: the night shift ends at
 * 02:00, which is *before* it starts, and anyone reading "16:00 – 02:00" as a fourteen-hour gap
 * would mis-plan the roster. So it carries a moon badge and prints its real length beside it.
 */
export function ShiftMasterPanel() {
  const qc = useQueryClient();
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  const { data: shifts = [], isLoading } = useQuery({
    queryKey: ['hr', 'shifts', showInactive],
    queryFn: async () => (await api.get<Shift[]>('/hr/shifts', { params: { includeInactive: showInactive } })).data,
  });

  const save = useMutation({
    mutationFn: async (body: any) => body.id
      ? (await api.patch(`/hr/shifts/${body.id}`, body)).data
      : (await api.post('/hr/shifts', body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'shifts'] });
      toast.success(editing?.id ? 'Shift updated' : 'Shift created');
      setEditing(null);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save the shift'),
  });

  const remove = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/hr/shifts/${id}`)).data,
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['hr', 'shifts'] });
      // Deleting a shift somebody is on would blank it on their record, so the server deactivates
      // instead — say so plainly rather than reporting a delete that did not happen.
      toast.success(r?.deactivated
        ? `Shift deactivated — ${r.employeeCount} employee(s) are still on it, so it was kept for their records.`
        : 'Shift deleted');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not remove the shift'),
  });

  const totalStaffed = shifts.reduce((s, x) => s + (x.employeeCount ?? 0), 0);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Working Shifts</h3>
          <p className="text-xs text-slate-500">
            Assign one to each employee under Employees → Employment. Attendance is judged against the shift&apos;s start time.
          </p>
        </div>
        <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="rounded" />
          Show inactive
        </label>
        <button onClick={() => setEditing({ ...blank })}
          className="px-3 py-2 bg-primary text-white rounded-lg text-sm flex items-center gap-1.5">
          <Plus size={14} /> New Shift
        </button>
      </div>

      {isLoading ? (
        <div className="py-10 text-center text-slate-400 text-sm"><Loader2 size={16} className="inline animate-spin" /> Loading…</div>
      ) : shifts.length === 0 ? (
        <div className="py-10 text-center text-sm text-slate-500 border border-dashed border-slate-300 rounded-xl">
          No shifts yet — create one with the button above.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {shifts.map((s) => (
            <div key={s.id}
              className={`border rounded-xl p-4 ${s.isActive ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-70'}`}>
              <div className="flex items-start gap-2 mb-2">
                <span className={`mt-0.5 shrink-0 ${s.crossesMidnight ? 'text-indigo-600' : 'text-amber-500'}`}>
                  {s.crossesMidnight ? <Moon size={16} /> : <Sun size={16} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 text-sm truncate">{s.name}</div>
                  {s.description && <div className="text-[11px] text-slate-500 truncate">{s.description}</div>}
                </div>
                {!s.isActive && <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded shrink-0">Inactive</span>}
              </div>

              <div className="flex items-center gap-1.5 text-sm text-slate-800 font-medium">
                <Clock size={13} className="text-slate-400" /> {s.label}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500">
                {s.durationLabel}
                {s.crossesMidnight && <span className="ml-1.5 text-indigo-600">· ends next day</span>}
              </div>

              <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center gap-2">
                <span className="flex items-center gap-1 text-xs text-slate-600">
                  <Users size={12} className="text-slate-400" />
                  {s.employeeCount} {s.employeeCount === 1 ? 'employee' : 'employees'}
                </span>
                <button onClick={() => setEditing({ ...s })}
                  className="ml-auto text-xs text-primary hover:underline">Edit</button>
                <button onClick={() => remove.mutate(s.id)} disabled={remove.isPending}
                  title={s.employeeCount > 0 ? 'People are on this shift — it will be deactivated, not deleted' : 'Delete this shift'}
                  className="text-xs text-rose-600 hover:underline disabled:opacity-50 flex items-center gap-0.5">
                  {s.employeeCount > 0 ? <><Power size={11} /> Deactivate</> : <><Trash2 size={11} /> Delete</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {shifts.length > 0 && (
        <p className="mt-3 text-xs text-slate-500">
          {totalStaffed} employee{totalStaffed === 1 ? '' : 's'} assigned across {shifts.filter((s) => s.isActive).length} active shift
          {shifts.filter((s) => s.isActive).length === 1 ? '' : 's'}.
        </p>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">{editing.id ? 'Edit Shift' : 'New Shift'}</h3>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-[11px] text-slate-500 mb-0.5">Shift Name *</label>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="Morning Shift (Office)"
                  className="w-full px-2.5 py-1.5 border border-slate-300 rounded text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] text-slate-500 mb-0.5">Start Time *</label>
                  <input type="time" value={editing.startTime} onChange={(e) => setEditing({ ...editing, startTime: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-slate-300 rounded text-sm" />
                </div>
                <div>
                  <label className="block text-[11px] text-slate-500 mb-0.5">End Time *</label>
                  <input type="time" value={editing.endTime} onChange={(e) => setEditing({ ...editing, endTime: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-slate-300 rounded text-sm" />
                </div>
              </div>

              {/* Said out loud while they are typing, because an end time earlier than the start looks
                  like a mistake unless the form confirms it was understood. */}
              {editing.startTime && editing.endTime && editing.endTime <= editing.startTime && (
                <div className="flex items-start gap-2 text-[11px] text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-lg px-2.5 py-2">
                  <Moon size={13} className="mt-px shrink-0" />
                  <span>This shift ends the <b>next day</b> — it will be recorded as running past midnight.</span>
                </div>
              )}

              <div>
                <label className="block text-[11px] text-slate-500 mb-0.5">Description</label>
                <input value={editing.description ?? ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  placeholder="Office employees"
                  className="w-full px-2.5 py-1.5 border border-slate-300 rounded text-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={editing.isActive !== false}
                  onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })} className="rounded" />
                Active — can be assigned to employees
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-sm text-slate-600">Cancel</button>
              <button onClick={() => save.mutate(editing)} disabled={!editing.name?.trim() || save.isPending}
                className="px-4 py-1.5 bg-primary text-white rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-50">
                {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
